import { useState, useEffect } from "react";
import { Table, Typography, Space, Input, Badge, Tooltip, Button, Popconfirm, message, Image, Tag } from "antd";
import { SearchOutlined, PlusOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import axios from "axios";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import config from "../../api_config.json";
import typologiesQuery from "./queries/typologiesQuery";
import deleteTypologyMutation from "./queries/deleteTypologyMutation";
import ParseLogo from "../shared/images/parse.png";
import CreateTypologyModal from "./CreateTypologyModal";
import EditTypologyModal from "./EditTypologyModal";

const { Title } = Typography;
const { Search } = Input;

const MarketplaceTypologies = () => {
  const [loading, setLoading] = useState(true);
  const [typologies, setTypologies] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedTypology, setSelectedTypology] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  const fetchTypologies = async (page = 1, pageSize = 20, search = "") => {
    setLoading(true);
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      const where = {};
      if (search) {
        where.name = { matchesRegex: search, options: "i" };
      }

      const variables = {
        first: pageSize,
        skip: (page - 1) * pageSize,
        where: Object.keys(where).length > 0 ? where : undefined,
        order: ["name_ASC"],
      };

      const response = await axios.post(
        graphqlUrl,
        {
          query: typologiesQuery,
          variables,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const edges = response.data?.marketplace_typologies?.edges || [];
      const count = response.data?.marketplace_typologies?.count || 0;

      setTypologies(edges.map((edge) => edge.node));
      setPagination((prev) => ({ ...prev, current: page, pageSize, total: count }));
    } catch (error) {
      console.error("Error fetching marketplace typologies:", error);
      setTypologies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTypologies(pagination.current, pagination.pageSize, searchValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (newPagination) => {
    fetchTypologies(newPagination.current, newPagination.pageSize, searchValue);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchTypologies(1, pagination.pageSize, value);
  };

  const handleCreateTypology = () => {
    setCreateModalVisible(false);
    // Refresh the list after creating
    fetchTypologies(1, pagination.pageSize, searchValue);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleEditTypology = (typology) => {
    setSelectedTypology(typology);
    setEditModalVisible(true);
  };

  const handleUpdateTypology = () => {
    setEditModalVisible(false);
    setSelectedTypology(null);
    // Refresh the list after updating
    fetchTypologies(pagination.current, pagination.pageSize, searchValue);
  };

  const handleDelete = async (typologyId) => {
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      await axios.post(
        graphqlUrl,
        {
          query: deleteTypologyMutation,
          variables: { id: typologyId },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      message.success("Tipologia eliminata con successo");
      // Refresh the list
      fetchTypologies(pagination.current, pagination.pageSize, searchValue);
    } catch (error) {
      console.error("Error deleting typology:", error);
      message.error("Errore durante l'eliminazione della tipologia");
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "objectId",
      key: "objectId",
      width: 100,
    },
    {
      title: "Nome",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Modalità",
      key: "modality",
      render: (_, record) => (
        <Space size="small">
          {record.b2b && <Tag color="blue">B2B</Tag>}
          {record.b2c && <Tag color="green">B2C</Tag>}
        </Space>
      ),
    },
    {
      title: "Icona",
      dataIndex: "icon",
      key: "icon",
      render: (icon) =>
        icon?.url ? (
          <Image
            src={icon.url}
            alt={icon.name}
            width={40}
            height={40}
            style={{ objectFit: "cover" }}
          />
        ) : (
          "-"
        ),
    },
    {
      title: "Copertina",
      dataIndex: "cover",
      key: "cover",
      render: (cover) =>
        cover?.url ? (
          <Image
            src={cover.url}
            alt={cover.name}
            width={60}
            height={40}
            style={{ objectFit: "cover" }}
          />
        ) : (
          "-"
        ),
    },
    {
      title: "Data Creazione",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleDateString("it-IT"),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Azioni",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditTypology(record)}
          >
            Modifica
          </Button>
          <Popconfirm
            title="Sei sicuro di voler eliminare questa tipologia?"
            onConfirm={() => handleDelete(record.objectId)}
            okText="Si"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Elimina
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
    {
      title: "Links",
      key: "links",
      render: (_, record) => {
        const parseDashboardUrl = `${config.parseDashboard[isProd ? "prod" : "dev"]}/browser/marketplace_typology?filters=[{"field":"objectId","constraint":"eq","compareTo":"${record.objectId}"}]`;
        return (
          <Tooltip title="Visualizza in Parse Dashboard" placement="top">
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={parseDashboardUrl}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                backgroundColor: 'transparent',
                transition: 'all 0.2s ease',
              }}
            >
              <img
                src={ParseLogo}
                width={20}
                alt="Parse Dashboard"
                style={{ display: 'block' }}
              />
            </a>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div style={{ margin: "16px 16px" }}>
      <div style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Title level={3} style={{ margin: 0 }}>
              <Space>
                Tipologie Marketplace
                <Badge overflowCount={5000} count={pagination.total} />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setCreateModalVisible(true)}
                >
                  Crea Tipologia
                </Button>
              </Space>
            </Title>
            <Search
              placeholder="Cerca per nome"
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              onSearch={handleSearch}
              style={{ width: 400 }}
            />
          </div>
        </Space>
      </div>

      <Table
        dataSource={typologies}
        columns={columns}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        rowKey="objectId"
        scroll={{ x: "max-content" }}
      />

      <CreateTypologyModal
        visible={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        onCreate={handleCreateTypology}
      />

      <EditTypologyModal
        visible={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setSelectedTypology(null);
        }}
        onUpdate={handleUpdateTypology}
        typology={selectedTypology}
      />
    </div>
  );
};

export default MarketplaceTypologies;
